import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type {
  AdjustmentScenario,
  CreateAdjustmentRequest,
  UpdateAdjustmentRequest,
  PositionChange,
} from '@/types/api'
import { apiClient } from '@/lib/api-client'

/**
 * Query key factory for adjustments
 */
export const adjustmentKeys = {
  all: ['adjustments'] as const,
  lists: () => [...adjustmentKeys.all, 'list'] as const,
  list: (filters: { id?: string } = {}) =>
    [...adjustmentKeys.lists(), filters] as const,
  details: () => [...adjustmentKeys.all, 'detail'] as const,
  detail: (id: string) => [...adjustmentKeys.details(), id] as const,
}

/**
 * Hook to fetch all adjustment scenarios
 * 
 * Features:
 * - 1 minute stale time for fresh data
 * - Automatic background refetching
 * - Error handling with ApiError
 */
export function useAdjustments() {
  return useQuery({
    queryKey: adjustmentKeys.lists(),
    queryFn: () => apiClient.getAdjustments(),
    staleTime: 1000 * 60, // 1 minute
    gcTime: 1000 * 60 * 5, // 5 minutes
  })
}

/**
 * Hook to create a new adjustment scenario
 * 
 * Features:
 * - Optimistic update: immediately adds to list
 * - Rollback on error
 * - Invalidates adjustments query on success
 */
export function useCreateAdjustment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['adjustments', 'create'],
    mutationFn: (data: CreateAdjustmentRequest) =>
      apiClient.createAdjustment(data),
    onMutate: async (newAdjustment) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: adjustmentKeys.lists() })

      // Snapshot previous value
      const previousAdjustments = queryClient.getQueryData<AdjustmentScenario[]>(
        adjustmentKeys.lists()
      )

      // Optimistically update
      const optimisticAdjustment: AdjustmentScenario = {
        ...newAdjustment,
        id: `optimistic-${Date.now()}`,
      }

      queryClient.setQueryData<AdjustmentScenario[]>(
        adjustmentKeys.lists(),
        (old) => (old ? [optimisticAdjustment, ...old] : [optimisticAdjustment])
      )

      return { previousAdjustments }
    },
    onError: (_err, _newAdjustment, context) => {
      // Rollback on error
      if (context?.previousAdjustments) {
        queryClient.setQueryData(
          adjustmentKeys.lists(),
          context.previousAdjustments
        )
      }
    },
    onSettled: () => {
      // Always refetch after error or success
      queryClient.invalidateQueries({ queryKey: adjustmentKeys.lists() })
    },
  })
}

/**
 * Hook to update an existing adjustment scenario
 * 
 * Features:
 * - Optimistic update
 * - Rollback on error
 * - Invalidates specific adjustment query
 */
export function useUpdateAdjustment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['adjustments', 'update'],
    mutationFn: ({ id, data }: { id: string; data: UpdateAdjustmentRequest }) =>
      apiClient.updateAdjustment(id, data),
    onMutate: async ({ id, data }) => {
      await queryClient.cancelQueries({ queryKey: adjustmentKeys.lists() })
      await queryClient.cancelQueries({ queryKey: adjustmentKeys.detail(id) })

      const previousAdjustments = queryClient.getQueryData<AdjustmentScenario[]>(
        adjustmentKeys.lists()
      )

      // Optimistically update
      queryClient.setQueryData<AdjustmentScenario[]>(
        adjustmentKeys.lists(),
        (old) =>
          old?.map((adjustment) =>
            adjustment.id === id ? { ...adjustment, ...data } : adjustment
          ) ?? []
      )

      return { previousAdjustments }
    },
    onError: (_err, _vars, context) => {
      if (context?.previousAdjustments) {
        queryClient.setQueryData(
          adjustmentKeys.lists(),
          context.previousAdjustments
        )
      }
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: adjustmentKeys.lists() })
      queryClient.invalidateQueries({
        queryKey: adjustmentKeys.detail(variables.id),
      })
    },
  })
}

/**
 * Hook to delete an adjustment scenario
 * 
 * Features:
 * - Optimistic removal from list
 * - Rollback on error
 * - Confirmation should be handled by UI before calling
 */
export function useDeleteAdjustment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['adjustments', 'delete'],
    mutationFn: (id: string) => apiClient.deleteAdjustment(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: adjustmentKeys.lists() })

      const previousAdjustments = queryClient.getQueryData<AdjustmentScenario[]>(
        adjustmentKeys.lists()
      )

      // Optimistically remove
      queryClient.setQueryData<AdjustmentScenario[]>(
        adjustmentKeys.lists(),
        (old) => old?.filter((adjustment) => adjustment.id !== id) ?? []
      )

      return { previousAdjustments }
    },
    onError: (_err, _id, context) => {
      if (context?.previousAdjustments) {
        queryClient.setQueryData(
          adjustmentKeys.lists(),
          context.previousAdjustments
        )
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: adjustmentKeys.lists() })
    },
  })
}

/**
 * Utility function to validate position changes
 * Ensures at least one position has a change
 */
export function validatePositionChanges(changes: PositionChange[]): boolean {
  return changes.length > 0
}

/**
 * Utility function to check if adjustment name is unique
 * (should be called after fetching existing adjustments)
 */
export function isUniqueName(
  name: string,
  existingAdjustments: AdjustmentScenario[],
  excludeId?: string
): boolean {
  return !existingAdjustments.some(
    (adj) =>
      adj.name.toLowerCase() === name.toLowerCase() && adj.id !== excludeId
  )
}
